import React, {useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureRunDataEntryMode, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  BaseCondition,
  Condition,
  ConditionGroup,
  isCondition,
  isConditionGroup,
  useConditionsAndExperimentalInputs,
  useExperimentalInputsContextProperties,
} from 'studies/typed/shared/useExperimentDesignState';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import {EditNumConditionsButtons} from './EditNumConditionsButtons';
import {EditNumPlannedConditionsButtons} from './EditNumPlannedConditionsButtons';
import {ExperimentFlowchart, LargestTableTypeSizes} from './ExperimentalInputs';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {MethodExecutionLimitState, useMethodExecutionLimitState} from './useMethodExecutionLimitState';

/**
 * Represents the flowchart version with required properties for study design.
 */
export interface StudyDesignFlowchartVersion {
  id: string;
  largestTableTypeSizes: ReadonlyArray<{tableKind: string; size: number}>;
}

/**
 * Parameters for the useStudyDesignState hook.
 */
export interface UseStudyDesignStateParams<TCondition extends BaseCondition> {
  /** Whether this holds a procedure run or a procedure run plan */
  isPlan: boolean;
  /** Initial experimental inputs extracted from procedure run or plan */
  initialExperimentalInputs: ExperimentalInputs;
  /** Template flowchart with method nodes */
  templateFlowchart: ExperimentFlowchart;
  /** Initial conditions (from procedure run conditions or plan conditions) */
  initialConditions: Array<TCondition>;
  /**
   * Factory for the fallback default conditions, used when stored conditions are missing or out of
   * sync with the current procedure. Must produce values matching `TCondition` (e.g.
   * {@link createDefaultRunConditions} for runs, {@link createDefaultPlanConditions} for plans).
   */
  createDefaultConditions: (procedureType: ProcedureType) => Array<TCondition>;
  /** Procedure type (RECIPE or ASSAY) */
  procedureType: ProcedureType;
  /** Flowchart version with table size info */
  flowchartVersion: StudyDesignFlowchartVersion;
  /** Data entry mode for the run/plan */
  dataEntryMode: ProcedureRunDataEntryMode;
  /** Procedure ID */
  procedureId: string;
  /** Whether the design tab should initialize in edit mode */
  initialEditMode?: boolean;
  /**
   * Whether to select the first method on mount. The legacy design editor relies on a method being
   * selected synchronously on open. When `false`, the hook starts with no method selected
   * (`selectedMethodId === null`). Defaults to `true` to preserve legacy behavior.
   */
  selectFirstMethodInitially?: boolean;
}

/**
 * Result of the useStudyDesignState hook.
 */
export interface UseStudyDesignStateResult<TCondition extends BaseCondition> {
  // UI state
  selectedMethodId: string | null;
  setSelectedMethodId: (id: string | null) => void;
  editState: StudyDesignTabEditState;
  setEditState: (state: StudyDesignTabEditState) => void;
  selectedDesignTabIndex: number | null;
  setSelectedDesignTabIndex: (index: number | null) => void;
  sidepanelName: string;
  setSidepanelName: (name: string) => void;

  // Conditions and inputs
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (inputs: ExperimentalInputs) => void;
  resetConditionsAndInputs: (conditions: Array<TCondition>, experimentalInputs: ExperimentalInputs) => void;

  // Derived state
  context: ExperimentalInputsContextProperties;
  methodExecutionLimitState: MethodExecutionLimitState;
  templateFlowchart: ExperimentFlowchart;

  // UI elements
  editNumColsButtons: React.ReactNode;
}

const PARAMETER_VALUES_TAB_NAME = 'Conditions';

/**
 * Hook that encapsulates shared state management for study design tabs.
 * Used by both ProcedureRunDesignTabContents and ProcedureRunPlanDesignTabContents.
 */
export function useStudyDesignState<TCondition extends Condition | ConditionGroup>(
  params: UseStudyDesignStateParams<TCondition>
): UseStudyDesignStateResult<TCondition> {
  const {
    initialExperimentalInputs,
    templateFlowchart,
    initialConditions,
    createDefaultConditions,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
    isPlan,
    initialEditMode = false,
    selectFirstMethodInitially = true,
  } = params;

  // UI state
  const isEmptyFlowchart = templateFlowchart.nodes.length === 0;
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(
    selectFirstMethodInitially && !isEmptyFlowchart ? templateFlowchart.nodes[0].id : null
  );
  // Use useStateIfMounted because setEditState may be called in async callbacks
  const [editState, setEditState] = useStateIfMounted(
    initialEditMode ? StudyDesignTabEditState.EDITING : StudyDesignTabEditState.SAVED
  );
  const [selectedDesignTabIndex, setSelectedDesignTabIndex] = useState<number | null>(0);
  const [sidepanelName, setSidepanelName] = useState<string>(PARAMETER_VALUES_TAB_NAME);

  // Conditions and experimental inputs state
  const {conditions, setConditions, experimentalInputs, setExperimentalInputs, resetConditionsAndInputs} =
    useConditionsAndExperimentalInputs<TCondition>(
      {
        id: procedureId,
        procedureType,
        methods: templateFlowchart.nodes,
      },
      createDefaultConditions,
      procedureId,
      initialConditions,
      initialExperimentalInputs
    );

  // useConditionsAndExperimentalInputs returns null when procedure ID is null,
  // which should never be the case when using this hook
  assert(
    conditions != null && experimentalInputs != null,
    'Expected conditions and experimentalInputs to be non-null'
  );

  // Context for ExperimentalInputs components
  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    setExperimentalInputs,
    templateFlowchart,
    procedureType,
    // Should be locked when not in editing state
    editState !== StudyDesignTabEditState.EDITING,
    isPlan
  );
  assert(context != null, 'Expected context to be non-null');

  // Method execution limit state
  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditions,
    flowchartVersion.largestTableTypeSizes as LargestTableTypeSizes,
    dataEntryMode
  );

  // Edit buttons for conditions
  let editNumColsButtons: React.ReactNode;
  if (isPlan) {
    assert(
      conditions.every((c) => isConditionGroup(c)),
      'Expected all conditions to be ConditionGroups for plans'
    );
    const planConditions = conditions as Array<ConditionGroup>;
    const setPlanConditions = setConditions as (
      conditions: Array<ConditionGroup>,
      prevIndexes: Array<number | null>
    ) => void;
    editNumColsButtons = (
      <EditNumPlannedConditionsButtons
        conditions={planConditions}
        setConditions={setPlanConditions}
        methodExecutionLimitState={methodExecutionLimitState}
        procedureType={procedureType}
        showReplicateLimitLabel={true}
      />
    );
  } else {
    assert(
      conditions.every((c) => isCondition(c)),
      'Expected all conditions to be Conditions for runs'
    );
    const runConditions: Array<Condition> = conditions;
    const setRunConditions = setConditions as (
      conditions: Array<Condition>,
      prevIndexes: Array<number | null>
    ) => void;
    editNumColsButtons = (
      <EditNumConditionsButtons
        conditions={runConditions}
        setConditions={setRunConditions}
        methodExecutionLimitState={methodExecutionLimitState}
        procedureType={procedureType}
        showReplicateLimitLabel={true}
      />
    );
  }

  return {
    // UI state
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,

    // Conditions and inputs
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    resetConditionsAndInputs,

    // Derived state
    context,
    methodExecutionLimitState,
    templateFlowchart,

    // UI elements
    editNumColsButtons,
  };
}

/**
 * Returns an array of indices indicating the positions of each initial condition
 * within the full list of conditions, based on matching condition IDs.
 * This is necessary to handle the case where the user reorders the persisted conditions.
 */
export function getInitialConditionIndices(
  initialConditions: Array<Condition>,
  conditions: Array<Condition>
): Array<number> {
  return initialConditions.map((initialCondition) =>
    conditions.findIndex((c) => c.id === initialCondition.id)
  );
}
